import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageIntro } from '@/components/help/PageIntro';
import { LabelWithHint } from '@/components/help/LabelWithHint';
import { Button } from '@/components/ui/button';
import { MediaPicker } from '@/components/social/MediaPicker';
import { PlatformIcon } from '@/components/social/PlatformIcon';
import { BrowserHandoffDialog } from '@/components/social/BrowserPublishingPanel';
import {
  socialApi,
  type BestTimeResult,
  type ContentPillar,
  type MediaAsset,
  type SlotSuggestion,
  type SocialAccount,
  type SocialPost,
  type SocialPostKind,
  type SocialPlatform,
  type SocialTargetMetadata,
  type TargetInput,
} from '@/lib/social-api';
import { engagementApi } from '@/lib/engagement-api';
import { formatMetric } from '@/lib/post-metrics';
import {
  PLATFORM_ORDER,
  POST_KIND_LABEL,
  platformMeta,
  sharedKinds,
  validateChannel,
  type ChannelWarning,
} from '@/lib/social-platforms';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  Link2,
  Image as ImageIcon,
  Loader2,
  RotateCcw,
  Save,
  Send,
  Sparkles,
  Trash2,
  Wand2,
  X,
} from 'lucide-react';

/** Ein gewählter Kanal mit optionalen Abweichungen vom Basistext. */
interface ChannelDraft {
  accountId: string;
  accountName: string;
  platform: SocialPlatform;
  targetId?: string;
  kind: SocialPostKind;
  bodyOverride: string | null;
  titleOverride: string | null;
  metadata: SocialTargetMetadata;
  /** YouTube-Sichtbarkeit; andere Kanäle steuern das serverseitig. */
  privacy: string | null;
  status?: string;
  errorMessage?: string | null;
  externalUrl?: string | null;
}

interface ComposerPageProps {
  postId?: string | null;
  onNavigate: (path: string) => void;
}

export function ComposerPage({ postId, onNavigate }: ComposerPageProps) {
  // Different posts must never share unsaved fields or the previous saved ID.
  return <ComposerForm key={postId ?? 'new'} postId={postId} onNavigate={onNavigate} />;
}

function ComposerForm({ postId, onNavigate }: ComposerPageProps) {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [pillars, setPillars] = useState<ContentPillar[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [adapting, setAdapting] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [improving, setImproving] = useState(false);
  const [showBrief, setShowBrief] = useState(false);
  const [aiBrief, setAiBrief] = useState('');

  // Basisinhalt
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [firstComment, setFirstComment] = useState('');
  const [kind, setKind] = useState<SocialPostKind>('feed');
  const [media, setMedia] = useState<MediaAsset[]>([]);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [browserHandoffPostId, setBrowserHandoffPostId] = useState<string | null>(null);

  // Planung
  const [scheduledAt, setScheduledAt] = useState('');
  const [pillarId, setPillarId] = useState<string | null>(null);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [boardStatus, setBoardStatus] = useState<SocialPost['board_status']>('draft');

  const [channels, setChannels] = useState<ChannelDraft[]>([]);
  const [activeTab, setActiveTab] = useState<string>('base');
  const [currentPostId, setCurrentPostId] = useState<string | null>(postId ?? null);
  const reviewedPostVersion = useRef<string | null>(null);
  const [newsletterEnabled, setNewsletterEnabled] = useState(false);
  const [newsletterListId, setNewsletterListId] = useState('');
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterFromName, setNewsletterFromName] = useState('Leftclick AG');
  const [newsletterReplyTo, setNewsletterReplyTo] = useState('');
  const [newsletterLists, setNewsletterLists] = useState<Array<{ id: string; name: string; member_count: number }>>([]);
  const [publishedMetrics, setPublishedMetrics] = useState<
    Array<{ target_id: string; platform?: string; analytics?: Record<string, number> }>
  >([]);

  // ── Laden ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const hash = window.location.hash.slice(1) || '';
    const qIndex = hash.indexOf('?');
    if (qIndex >= 0) {
      const params = new URLSearchParams(hash.slice(qIndex + 1));
      const cid = params.get('campaign_id');
      if (cid) setCampaignId(cid);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [accountList, pillarList] = await Promise.all([
          socialApi.listAccounts(),
          socialApi.listPillars().catch(() => [] as ContentPillar[]),
        ]);
        if (cancelled) return;
        setAccounts(accountList);
        setPillars(pillarList.filter((p) => p.is_active));
        socialApi.newsletterLists().then(setNewsletterLists).catch(() => setNewsletterLists([]));

        if (postId) {
          const post = await socialApi.getPost(postId);
          if (cancelled) return;
          setCurrentPostId(post.id);
          reviewedPostVersion.current = post.updated_at ?? null;
          setTitle(post.title || '');
          setBody(post.body || '');
          setLinkUrl(post.link_url || '');
          setFirstComment(post.first_comment || '');
          setKind(post.post_kind);
          setMedia(post.media ?? []);
          setPillarId(post.pillar_id ?? null);
          setBoardStatus(post.board_status ?? 'draft');
          if (post.campaign_id) setCampaignId(post.campaign_id);
          if (post.planned_at) setScheduledAt(toLocalInput(post.planned_at));
          setNewsletterEnabled(Boolean(post.newsletter_enabled));
          setNewsletterListId(post.newsletter_list_id || '');
          setNewsletterSubject(post.newsletter_subject || post.title || '');
          setNewsletterFromName(post.newsletter_from_name || 'Leftclick AG');
          setNewsletterReplyTo(post.newsletter_reply_to || '');
          if (post.targets.some((t) => t.status === 'published')) {
            socialApi.postAnalytics(post.id).then(setPublishedMetrics).catch(() => setPublishedMetrics([]));
          }

          const byId = new Map(accountList.map((a) => [a.id, a]));
          setChannels(
            post.targets.map((t) => ({
              accountId: t.social_account_id,
              accountName:
                t.social_accounts?.display_name || byId.get(t.social_account_id)?.display_name || 'Konto',
              platform: t.platform,
              targetId: t.id,
              kind: t.post_kind,
              bodyOverride: t.adapted_body ?? null,
              titleOverride: t.adapted_title ?? null,
              metadata: t.metadata ?? {},
              privacy: t.privacy_status ?? null,
              status: t.status,
              errorMessage: t.error_message,
              externalUrl: t.external_url,
            }))
          );
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Daten konnten nicht geladen werden.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [postId]);

  const activeAccounts = useMemo(() => accounts.filter((a) => a.status === 'active'), [accounts]);

  const accountsByPlatform = useMemo(() => {
    const map = new Map<SocialPlatform, SocialAccount[]>();
    for (const acc of activeAccounts) {
      const list = map.get(acc.platform);
      if (list) list.push(acc);
      else map.set(acc.platform, [acc]);
    }
    return map;
  }, [activeAccounts]);

  const selectedPlatforms = useMemo(
    () => Array.from(new Set(channels.map((c) => c.platform))),
    [channels]
  );

  const availableKinds = useMemo(() => sharedKinds(selectedPlatforms), [selectedPlatforms]);

  const hasVideo = media.some((m) => m.mime_type.startsWith('video/'));
  const contentLocked = channels.some((channel) =>
    ['scheduled', 'publishing', 'pending_approval', 'published'].includes(channel.status ?? '')
  );

  const warningsByChannel = useMemo(() => {
    const map = new Map<string, ChannelWarning[]>();
    for (const ch of channels) {
      if (ch.status === 'published') continue;
      map.set(
        ch.accountId,
        [...(accounts.find((account) => account.id === ch.accountId)?.status !== 'active'
          ? [{ level: 'error' as const, message: 'Konto ist nicht verbunden. Bitte im Social Hub neu verbinden.' }]
          : []), ...validateChannel({
          platform: ch.platform,
          kind: ch.kind,
          body: ch.bodyOverride ?? body,
          title: ch.titleOverride ?? title,
          mediaCount: media.length,
          hasVideo,
          subreddit: ch.metadata.subreddit,
          boardId: ch.metadata.board_id,
        })]
      );
    }
    return map;
  }, [channels, accounts, body, title, media.length, hasVideo]);

  const blockingErrors = useMemo(
    () =>
      channels.flatMap((ch) =>
        (warningsByChannel.get(ch.accountId) ?? [])
          .filter((w) => w.level === 'error')
          .map((w) => `${platformMeta(ch.platform).label}: ${w.message}`)
      ),
    [channels, warningsByChannel]
  );

  // ── Kanal-Handling ───────────────────────────────────────────────────────
  const toggleAccount = (account: SocialAccount) => {
    setChannels((prev) => {
      const existing = prev.find((c) => c.accountId === account.id);
      if (existing) {
        if (existing.status === 'published' || existing.status === 'publishing') {
          toast.error('Veröffentlichte oder gerade laufende Kanäle lassen sich nicht entfernen.');
          return prev;
        }
        if (activeTab === account.id) setActiveTab('base');
        return prev.filter((c) => c.accountId !== account.id);
      }
      const meta = platformMeta(account.platform);
      return [
        ...prev,
        {
          accountId: account.id,
          accountName: account.display_name,
          platform: account.platform,
          kind: meta.kinds.includes(kind) ? kind : (meta.kinds[0] ?? 'feed'),
          bodyOverride: null,
          titleOverride: null,
          metadata: {},
          privacy: account.platform === 'youtube' ? 'private' : null,
        },
      ];
    });
  };

  const patchChannel = (accountId: string, patch: Partial<ChannelDraft>) => {
    setChannels((prev) => prev.map((c) => (c.accountId === accountId ? { ...c, ...patch } : c)));
  };

  const patchChannelMeta = (accountId: string, patch: SocialTargetMetadata) => {
    setChannels((prev) =>
      prev.map((c) => (c.accountId === accountId ? { ...c, metadata: { ...c.metadata, ...patch } } : c))
    );
  };

  const changeKind = (nextKind: SocialPostKind) => {
    setKind(nextKind);
    setChannels((previous) => previous.map((channel) =>
      channel.kind === kind && platformMeta(channel.platform).kinds.includes(nextKind)
        ? { ...channel, kind: nextKind }
        : channel
    ));
  };

  const buildTargets = useCallback(
    (): TargetInput[] =>
      channels.map((c) => ({
        social_account_id: c.accountId,
        platform: c.platform,
        post_kind: c.kind,
        adapted_body: c.bodyOverride,
        adapted_title: c.titleOverride,
        privacy_status: c.privacy,
        metadata: c.metadata,
      })),
    [channels]
  );

  // ── Speichern / Veröffentlichen ──────────────────────────────────────────
  const applyPostStatus = useCallback((post: SocialPost) => {
    setBoardStatus(post.board_status ?? 'draft');
    setChannels((previous) => previous.map((channel) => {
      const target = post.targets.find((row) => row.social_account_id === channel.accountId);
      return target ? {
        ...channel,
        targetId: target.id,
        status: target.status,
        errorMessage: target.error_message,
        externalUrl: target.external_url,
      } : channel;
    }));
  }, []);

  const persist = useCallback(async (): Promise<string> => {
    if (contentLocked && currentPostId) return currentPostId;
    const payload = {
      title: title.trim() || null,
      body: body.trim(),
      post_kind: kind,
      link_url: linkUrl.trim() || null,
      first_comment: firstComment.trim() || null,
      campaign_id: campaignId || null,
      media_asset_ids: media.map((m) => m.id),
      pillar_id: pillarId,
      planned_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      targets: buildTargets(),
      newsletter_enabled: newsletterEnabled,
      newsletter_list_id: newsletterListId || null,
      newsletter_subject: newsletterSubject || null,
      newsletter_from_name: newsletterFromName || null,
      newsletter_reply_to: newsletterReplyTo || null,
    };

    if (currentPostId) {
      const saved = await socialApi.updatePost(currentPostId, payload);
      reviewedPostVersion.current = saved.updated_at ?? null;
      applyPostStatus(saved);
      return currentPostId;
    }
    const created = await socialApi.createPost({ ...payload, board_status: 'draft' });
    reviewedPostVersion.current = created.updated_at ?? null;
    setCurrentPostId(created.id);
    applyPostStatus(created);
    return created.id;
  }, [
    title,
    body,
    kind,
    linkUrl,
    firstComment,
    campaignId,
    media,
    pillarId,
    scheduledAt,
    buildTargets,
    currentPostId,
    contentLocked,
    newsletterEnabled,
    newsletterListId,
    newsletterSubject,
    newsletterFromName,
    newsletterReplyTo,
    applyPostStatus,
  ]);

  const run = async (mode: 'draft' | 'schedule' | 'publish' | 'approve') => {
    if (mode !== 'draft') {
      if (!channels.length && !newsletterEnabled) {
        toast.error('Bitte mindestens einen Kanal oder Newsletter wählen.');
        return;
      }
      if (channels.length && blockingErrors.length) {
        toast.error(blockingErrors[0]);
        return;
      }
      if (newsletterEnabled && (!newsletterListId || !newsletterSubject.trim() || !newsletterReplyTo.trim())) {
        toast.error('Newsletter braucht Liste, Betreff und Reply-To.');
        return;
      }
    }
    if (mode === 'schedule' && (!scheduledAt || !Number.isFinite(new Date(scheduledAt).getTime()) || new Date(scheduledAt).getTime() <= Date.now())) {
      toast.error('Bitte ein Datum und eine Uhrzeit in der Zukunft wählen.');
      return;
    }

    let persistedId = currentPostId;
    setSaving(true);
    try {
      const id = await persist();
      persistedId = id;
      if (mode === 'schedule') {
        const scheduled = await socialApi.schedulePost(id, new Date(scheduledAt).toISOString());
        applyPostStatus(scheduled);
        if (scheduled.targets.some((target) => target.status === 'pending_approval')) {
          toast.message('Termin gespeichert. Der Beitrag wartet auf Freigabe.');
        } else {
          toast.success('Beitrag geplant.');
        }
        onNavigate('/planner');
        return;
      }
      if (mode === 'publish') {
        const published = await socialApi.publishPost(id);
        applyPostStatus(published);
        if (published.targets.some((target) => target.status === 'failed') || published.newsletter_status === 'failed') {
          toast.warning('Veröffentlichung mit Fehlern. Details in der Warteschlange prüfen.');
        } else {
          toast.success('Veröffentlichung gestartet.');
        }
        onNavigate('/social/queue');
        return;
      }
      if (mode === 'approve') {
        if (!reviewedPostVersion.current) throw new Error('Beitrag neu laden und vor der Freigabe prüfen.');
        applyPostStatus(await socialApi.approvePost(id, reviewedPostVersion.current));
        toast.success('Beitrag freigegeben.');
        return;
      }
      toast.success('Entwurf gespeichert.');
      if (!postId) onNavigate(`/composer/${id}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Aktion fehlgeschlagen.');
      if (mode !== 'draft' && persistedId) {
        // Refresh delivery status only. Never advance the reviewed content
        // revision here: the displayed text/media have not been reloaded.
        await socialApi.getPost(persistedId).then(applyPostStatus).catch(() => undefined);
      }
    } finally {
      setSaving(false);
    }
  };

  /** Lässt die KI den Basistext pro Kanal zuschneiden. */
  const adaptAll = async () => {
    if (!body.trim()) {
      toast.error('Bitte zuerst einen Basistext schreiben.');
      return;
    }
    setAdapting(true);
    try {
      const id = await persist();
      const { post: updated, meta } = await socialApi.adaptPost(id);
      setChannels((prev) =>
        prev.map((c) => {
          const target = updated.targets.find((t) => t.social_account_id === c.accountId);
          return target ? { ...c, targetId: target.id, bodyOverride: target.adapted_body ?? null } : c;
        })
      );
      // Without this the truncation fallback looks identical to a real rewrite.
      if (meta && meta.ai_applied_count < meta.total) {
        toast.warning(
          meta.ai_applied_count === 0
            ? `Keine KI-Anpassung: ${meta.ai_unavailable_reason ?? 'KI nicht verfügbar.'} Texte wurden nur gekürzt.`
            : `${meta.ai_applied_count} von ${meta.total} Kanälen angepasst – der Rest wurde nur gekürzt.`
        );
      } else {
        toast.success('Texte pro Kanal angepasst.');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Anpassen fehlgeschlagen.');
    } finally {
      setAdapting(false);
    }
  };

  /** Schreibt den Basistext aus einem kurzen Briefing (ModelRouter). */
  const draftFromBrief = async () => {
    if (!aiBrief.trim()) {
      toast.error('Bitte zuerst ein kurzes Briefing eingeben.');
      return;
    }
    setDrafting(true);
    try {
      const { body: generated } = await socialApi.draftText({
        brief: aiBrief,
        platforms: channels.map((c) => c.platform),
        kind,
      });
      setBody(generated);
      setShowBrief(false);
      toast.success('Entwurf erstellt.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Entwurf fehlgeschlagen.');
    } finally {
      setDrafting(false);
    }
  };

  /** Überarbeitet den vorhandenen Basistext. */
  const improveBody = async () => {
    if (!body.trim()) {
      toast.error('Kein Text zum Überarbeiten.');
      return;
    }
    setImproving(true);
    try {
      const { body: improved } = await socialApi.improveText({ text: body });
      setBody(improved);
      toast.success('Text überarbeitet.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Überarbeiten fehlgeschlagen.');
    } finally {
      setImproving(false);
    }
  };

  const retryChannel = async (channel: ChannelDraft) => {
    if (!channel.targetId) return;
    setSaving(true);
    try {
      const target = await socialApi.retryTarget(channel.targetId);
      toast.success('Erneuter Versuch gestartet.');
      patchChannel(channel.accountId, { status: target.status, errorMessage: target.error_message });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retry fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const publishChannel = async (channel: ChannelDraft) => {
    const error = warningsByChannel.get(channel.accountId)?.find((warning) => warning.level === 'error');
    if (error) {
      toast.error(error.message);
      return;
    }
    setSaving(true);
    try {
      const id = await persist();
      const fresh = await socialApi.getPost(id);
      const target = fresh.targets.find((t) => t.social_account_id === channel.accountId);
      if (!target) throw new Error('Kanal wurde noch nicht gespeichert.');
      await socialApi.publishTarget(target.id);
      const updated = await socialApi.getPost(id);
      applyPostStatus(updated);
      toast.success(`${platformMeta(channel.platform).label} wird veröffentlicht.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Veröffentlichen fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div role="alert" className="rounded-lg border border-danger/30 bg-danger-soft p-5 text-sm text-danger">
        <p>{loadError}</p>
        <Button className="mt-3" variant="outline" onClick={() => window.location.reload()}>Erneut laden</Button>
      </div>
    );
  }

  const activeChannel = channels.find((c) => c.accountId === activeTab) ?? null;

  return (
    <div className="w-full max-w-none">
      <PageIntro
        title={currentPostId ? 'Beitrag bearbeiten' : 'Composer'}
        description="Medien hinzufügen, Kanäle wählen und veröffentlichen oder planen."
        className="max-w-none"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={saving || adapting || contentLocked} onClick={() => void run('draft')}>
              <Save size={16} className="mr-1.5" /> Entwurf
            </Button>
            {currentPostId && (
              <Button
                variant="outline"
                disabled={saving}
                onClick={() =>
                  engagementApi
                    .shareApproval({ kind: 'social_post', entity_id: currentPostId })
                    .then(async (share) => {
                      const url = `${window.location.origin}${window.location.pathname}#${share.path}`;
                      await navigator.clipboard.writeText(url);
                      toast.success('Freigabe-Link kopiert.');
                    })
                    .catch((e) => toast.error(e instanceof Error ? e.message : 'Link fehlgeschlagen.'))
                }
              >
                <Link2 size={16} className="mr-1.5" /> Link
              </Button>
            )}
            <Button variant="outline" disabled={saving || adapting} onClick={() => void run('approve')}>
              <CheckCircle2 size={16} className="mr-1.5" /> Freigeben
            </Button>
            <Button variant="outline" disabled={saving || adapting || contentLocked} onClick={() => void run('schedule')}>
              <CalendarClock size={16} className="mr-1.5" /> Planen
            </Button>
            <Button disabled={saving || adapting} onClick={() => void run('publish')}>
              {saving ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Send size={16} className="mr-1.5" />}
              Jetzt posten
            </Button>
            <Button variant="outline" disabled={saving || adapting || contentLocked} onClick={async () => {
              setSaving(true);
              try { setBrowserHandoffPostId(await persist()); }
              catch (error) { toast.error(error instanceof Error ? error.message : 'Beitrag konnte nicht gespeichert werden.'); }
              finally { setSaving(false); }
            }}>Im Browser übergeben</Button>
          </div>
        }
      />

      {contentLocked && currentPostId && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3 text-sm">
          <p className="text-muted">Dieser Beitrag ist bereits zur Veröffentlichung vorgesehen oder veröffentlicht. Änderungen als neuen Entwurf anlegen.</p>
          <Button variant="outline" size="sm" disabled={saving} onClick={async () => {
            setSaving(true);
            try {
              const copy = await socialApi.duplicatePost(currentPostId);
              onNavigate(`/composer/${copy.id}`);
            } catch (error) {
              toast.error(error instanceof Error ? error.message : 'Duplizieren fehlgeschlagen.');
            } finally {
              setSaving(false);
            }
          }}>Als neuen Entwurf kopieren</Button>
        </div>
      )}

      {activeAccounts.length === 0 && (
        <div className="mb-6 rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          Noch keine Social-Konten verbunden.{' '}
          <button
            type="button"
            className="font-semibold underline"
            onClick={() => onNavigate('/social/accounts')}
          >
            Konten verbinden
          </button>
        </div>
      )}

      {blockingErrors.length > 0 && (
        <div className="mb-6 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-danger">
            <AlertTriangle size={15} /> Vor dem Veröffentlichen zu klären
          </p>
          <ul className="mt-1.5 list-inside list-disc space-y-0.5 text-sm text-danger">
            {blockingErrors.slice(0, 5).map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Kanalauswahl */}
      <fieldset disabled={contentLocked || saving || adapting} className="mb-6 min-w-0">
        <LabelWithHint
          label="Kanäle"
          hint="Nur verbundene Konten mit Status „aktiv“. Ein Entwurf, beliebig viele Kanäle — jeder mit eigenem Text."
        />
        <div className="mt-2 flex flex-wrap gap-2">
          {PLATFORM_ORDER.flatMap((platform) => {
            const list = accountsByPlatform.get(platform) ?? [];
            return list.map((acc) => {
              const selected = channels.some((c) => c.accountId === acc.id);
              const meta = platformMeta(platform);
              return (
                <button
                  key={acc.id}
                  type="button"
                  onClick={() => toggleAccount(acc)}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition',
                    selected
                      ? 'border-border bg-primary text-primary-foreground'
                      : 'border-border text-muted hover:border-border hover:bg-card-hover'
                  )}
                >
                  <PlatformIcon platform={platform} size={22} />
                  <span className="text-xs font-medium">{meta.label}</span>
                  <span className="max-w-[10rem] truncate">{acc.display_name}</span>
                </button>
              );
            });
          })}
          {activeAccounts.length === 0 && (
            <p className="text-sm text-muted">Keine aktiven Konten verfügbar.</p>
          )}
        </div>
        <div className="mt-4 rounded-lg border border-border p-4">
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <input
              type="checkbox"
              checked={newsletterEnabled}
              onChange={(e) => setNewsletterEnabled(e.target.checked)}
            />
            Auch als Newsletter (Mailchimp)
          </label>
          {newsletterEnabled && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <LabelWithHint label="Liste" hint="Mailchimp-Audience aus der Integration." />
                <select
                  className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
                  value={newsletterListId}
                  onChange={(e) => setNewsletterListId(e.target.value)}
                >
                  <option value="">Liste wählen</option>
                  {newsletterLists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name} ({list.member_count})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <LabelWithHint label="Betreff" hint="E-Mail-Betreff, nicht der Social-Titel." />
                <input
                  className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
                  value={newsletterSubject}
                  onChange={(e) => setNewsletterSubject(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm text-muted">Absender</label>
                <input
                  className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
                  value={newsletterFromName}
                  onChange={(e) => setNewsletterFromName(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm text-muted">Reply-To</label>
                <input
                  type="email"
                  className="mt-1 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
                  value={newsletterReplyTo}
                  onChange={(e) => setNewsletterReplyTo(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      </fieldset>

      {/* Tabs: Basis + je Kanal */}
      <div className="mb-5 flex flex-wrap gap-1 border-b border-border pb-2">
        <TabButton active={activeTab === 'base'} onClick={() => setActiveTab('base')} label="Basisinhalt" />
        {channels.map((ch) => {
          const errors = (warningsByChannel.get(ch.accountId) ?? []).filter((w) => w.level === 'error');
          return (
            <TabButton
              key={ch.accountId}
              active={activeTab === ch.accountId}
              onClick={() => setActiveTab(ch.accountId)}
              label={platformMeta(ch.platform).label}
              sublabel={ch.accountName}
              badge={errors.length ? errors.length : undefined}
              done={ch.status === 'published'}
            />
          );
        })}
      </div>

      <div className="grid w-full gap-6 lg:grid-cols-[1fr_360px]">
        <div className="min-w-0 space-y-6">
          {activeTab === 'base' ? (
            <fieldset disabled={contentLocked || saving || adapting} className="min-w-0 space-y-6">
            <BaseEditor
              title={title}
              setTitle={setTitle}
              body={body}
              setBody={setBody}
              linkUrl={linkUrl}
              setLinkUrl={setLinkUrl}
              firstComment={firstComment}
              setFirstComment={setFirstComment}
              kind={kind}
              setKind={changeKind}
              availableKinds={availableKinds}
              media={media}
              onOpenMedia={() => setMediaPickerOpen(true)}
              onRemoveMedia={(id) => setMedia((prev) => prev.filter((m) => m.id !== id))}
              pillars={pillars}
              pillarId={pillarId}
              setPillarId={setPillarId}
              scheduledAt={scheduledAt}
              setScheduledAt={setScheduledAt}
              onAdapt={() => void adaptAll()}
              adapting={adapting}
              channelCount={channels.length}
              platforms={channels.map((c) => c.platform)}
              aiBrief={aiBrief}
              setAiBrief={setAiBrief}
              showBrief={showBrief}
              setShowBrief={setShowBrief}
              onDraft={() => void draftFromBrief()}
              drafting={drafting}
              onImprove={() => void improveBody()}
              improving={improving}
            />
            </fieldset>
          ) : activeChannel ? (
            <ChannelEditor
              channel={activeChannel}
              baseBody={body}
              baseTitle={title}
              warnings={warningsByChannel.get(activeChannel.accountId) ?? []}
              onPatch={(patch) => patchChannel(activeChannel.accountId, patch)}
              onPatchMeta={(patch) => patchChannelMeta(activeChannel.accountId, patch)}
              onRetry={() => void retryChannel(activeChannel)}
              onPublish={() => void publishChannel(activeChannel)}
              busy={saving}
              readOnly={contentLocked}
            />
          ) : null}
        </div>

        <PreviewPane
          channels={channels}
          activeChannel={activeChannel}
          baseBody={body}
          baseTitle={title}
          media={media}
          boardStatus={boardStatus}
          metrics={publishedMetrics}
        />
      </div>

      <MediaPicker
        open={mediaPickerOpen}
        onOpenChange={setMediaPickerOpen}
        selectedIds={media.map((m) => m.id)}
        selectedAssets={media}
        onConfirm={setMedia}
      />
      <BrowserHandoffDialog open={Boolean(browserHandoffPostId)} onOpenChange={open => { if (!open) setBrowserHandoffPostId(null); }} postId={browserHandoffPostId ?? undefined} />
    </div>
  );
}

// ─── Bausteine ───────────────────────────────────────────────────────────────

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const WEEKDAY_LABEL = ['', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/**
 * Terminvorschläge unter dem Datumsfeld: nächste freie Redaktions-Slots und
 * die Zeitfenster mit der besten Interaktion (aus den eigenen Analytics-Daten,
 * solange genug Beiträge vorliegen – sonst Richtwerte).
 */
function ScheduleAssist({
  platform,
  onPick,
}: {
  platform?: SocialPlatform;
  onPick: (iso: string) => void;
}) {
  const [slots, setSlots] = useState<SlotSuggestion[]>([]);
  const [best, setBest] = useState<BestTimeResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    void Promise.all([
      socialApi.nextSlots({ platform, count: 3 }).catch(() => [] as SlotSuggestion[]),
      socialApi.bestTimes({ platform, limit: 3 }).catch(() => null),
    ])
      .then(([nextSlots, bestTimes]) => {
        if (cancelled) return;
        setSlots(nextSlots);
        setBest(bestTimes);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, platform]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 text-xs font-medium text-muted underline-offset-2 hover:underline"
      >
        Terminvorschläge anzeigen
      </button>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-border p-3 text-xs">
      {loading && (
        <p className="flex items-center gap-1.5 text-muted">
          <Loader2 size={12} className="animate-spin" /> Vorschläge werden geladen …
        </p>
      )}

      {!loading && slots.length > 0 && (
        <div className="mb-2">
          <p className="mb-1 font-medium text-muted">Nächste freie Slots</p>
          <div className="flex flex-wrap gap-1.5">
            {slots.map((slot) => (
              <button
                key={`${slot.slot_id ?? 'best'}-${slot.at}`}
                type="button"
                onClick={() => onPick(slot.at)}
                className="rounded-md border border-border px-2 py-1 transition hover:border-border"
              >
                {new Date(slot.at).toLocaleString('de-CH', {
                  weekday: 'short',
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                <span className="ml-1 text-muted">{slot.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!loading && best && best.slots.length > 0 && (
        <div>
          <p className="mb-1 font-medium text-muted">
            Beste Zeiten{' '}
            <span className="font-normal text-muted">
              {best.source === 'data'
                ? `(${best.samples} eigene Beiträge)`
                : '(Richtwerte – zu wenige eigene Daten)'}
            </span>
          </p>
          <div className="flex flex-wrap gap-1.5 text-muted">
            {best.slots.map((slot) => (
              <span
                key={`${slot.weekday}-${slot.hour}`}
                className="rounded-md bg-card-hover px-2 py-1"
              >
                {WEEKDAY_LABEL[slot.weekday]} {String(slot.hour).padStart(2, '0')}:00
                {slot.samples > 0 && <span className="ml-1 text-muted">Ø {slot.avg_engagement}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {!loading && !slots.length && !best?.slots.length && (
        <p className="text-muted">Keine Vorschläge verfügbar. Lege Redaktions-Slots im Planner an.</p>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
  sublabel,
  badge,
  done,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  sublabel?: string;
  badge?: number;
  done?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition',
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted hover:bg-background dark:hover:bg-card-hover'
      )}
    >
      <span>{label}</span>
      {sublabel && <span className="max-w-[7rem] truncate text-xs opacity-60">{sublabel}</span>}
      {done && <CheckCircle2 size={13} className="text-success" />}
      {badge != null && (
        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-soft px-1 text-[10px] font-bold text-danger">
          {badge}
        </span>
      )}
    </button>
  );
}

function BaseEditor(props: {
  title: string;
  setTitle: (v: string) => void;
  body: string;
  setBody: (v: string) => void;
  linkUrl: string;
  setLinkUrl: (v: string) => void;
  firstComment: string;
  setFirstComment: (v: string) => void;
  kind: SocialPostKind;
  setKind: (k: SocialPostKind) => void;
  availableKinds: SocialPostKind[];
  media: MediaAsset[];
  onOpenMedia: () => void;
  onRemoveMedia: (id: string) => void;
  pillars: ContentPillar[];
  pillarId: string | null;
  setPillarId: (id: string | null) => void;
  scheduledAt: string;
  setScheduledAt: (v: string) => void;
  onAdapt: () => void;
  adapting: boolean;
  channelCount: number;
  platforms: SocialPlatform[];
  aiBrief: string;
  setAiBrief: (v: string) => void;
  showBrief: boolean;
  setShowBrief: (v: boolean) => void;
  onDraft: () => void;
  drafting: boolean;
  onImprove: () => void;
  improving: boolean;
}) {
  return (
    <>
      <section>
        <LabelWithHint
          label="Post-Art"
          hint="Es werden nur Arten angeboten, die alle gewählten Kanäle können. Einzelne Kanäle lassen sich im jeweiligen Tab abweichend einstellen."
        />
        <div className="mt-2 flex flex-wrap gap-2">
          {(Object.keys(POST_KIND_LABEL) as SocialPostKind[]).map((k) => {
            const supported = props.availableKinds.includes(k);
            return (
              <button
                key={k}
                type="button"
                disabled={!supported}
                title={supported ? undefined : 'Nicht von allen gewählten Kanälen unterstützt'}
                onClick={() => props.setKind(k)}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-sm font-medium transition',
                  props.kind === k
                    ? 'border-border bg-primary text-primary-foreground'
                    : 'border-border text-muted',
                  !supported && 'cursor-not-allowed opacity-35'
                )}
              >
                {POST_KIND_LABEL[k]}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <LabelWithHint htmlFor="composer-title" label="Titel" hint="Pflicht für YouTube (max. 100) und Reddit (max. 300). Andere Kanäle ignorieren ihn." />
        <input
          id="composer-title"
          value={props.title}
          onChange={(e) => props.setTitle(e.target.value)}
          className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
          placeholder="Titel des Beitrags"
          maxLength={300}
        />
      </section>

      <section>
        <div className="flex items-end justify-between gap-2">
          <LabelWithHint
            label="Basistext"
            htmlFor="composer-body"
            hint="Der gemeinsame Ausgangstext. Pro Kanal kannst du ihn im jeweiligen Tab überschreiben."
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => props.setShowBrief(!props.showBrief)}
              disabled={props.drafting}
            >
              {props.drafting ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <Sparkles size={14} className="mr-1.5" />
              )}
              Entwurf schreiben
            </Button>
            {props.body.trim().length > 0 && (
              <Button variant="outline" size="sm" onClick={props.onImprove} disabled={props.improving}>
                {props.improving ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Wand2 size={14} className="mr-1.5" />
                )}
                Verbessern
              </Button>
            )}
            {props.channelCount > 0 && (
              <Button variant="outline" size="sm" onClick={props.onAdapt} disabled={props.adapting}>
                {props.adapting ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Sparkles size={14} className="mr-1.5" />
                )}
                Pro Kanal anpassen
              </Button>
            )}
          </div>
        </div>

        {props.showBrief && (
          <div className="mt-2 rounded-lg border border-accent/30 bg-accent-soft p-3">
            <label
              htmlFor="composer-ai-brief"
              className="text-xs font-medium text-accent-soft-foreground"
            >
              Worum geht es? Ein bis zwei Sätze genügen.
            </label>
            <textarea
              id="composer-ai-brief"
              value={props.aiBrief}
              onChange={(e) => props.setAiBrief(e.target.value)}
              rows={3}
              className="mt-1.5 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
              placeholder="z. B. Neue Fallstudie mit Kunde X: 30 % weniger Supportaufwand nach Einführung."
            />
            <div className="mt-2 flex items-center gap-2">
              <Button size="sm" onClick={props.onDraft} disabled={props.drafting || !props.aiBrief.trim()}>
                {props.drafting && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                Text erzeugen
              </Button>
              <Button variant="ghost" size="sm" onClick={() => props.setShowBrief(false)}>
                Abbrechen
              </Button>
              {props.body.trim().length > 0 && (
                <span className="text-xs text-accent-soft-foreground/80">
                  Ersetzt den aktuellen Basistext.
                </span>
              )}
            </div>
          </div>
        )}

        <textarea
          id="composer-body"
          value={props.body}
          onChange={(e) => props.setBody(e.target.value)}
          rows={12}
          className="mt-2 w-full rounded-lg border border-border bg-card px-4 py-3 text-sm leading-relaxed"
          placeholder="Was möchtest du erzählen?"
        />
        <p className="mt-1 text-xs text-muted">{props.body.length} Zeichen</p>
      </section>

      <section>
        <div className="flex items-end justify-between gap-2">
          <LabelWithHint
            label="Medien"
            hint="Instagram, TikTok und YouTube brauchen zwingend Medien mit öffentlicher URL. Die Reihenfolge bestimmt das Carousel."
          />
          <Button variant="outline" size="sm" onClick={props.onOpenMedia}>
            <ImageIcon size={14} className="mr-1.5" /> Medien wählen
          </Button>
        </div>
        {props.media.length === 0 ? (
          <p className="mt-2 rounded-lg border border-dashed border-border px-4 py-6 text-center text-xs text-muted">
            Keine Medien gewählt — reine Textposts gehen nur auf LinkedIn, Facebook und Reddit.
          </p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-2">
            {props.media.map((asset, index) => (
              <div
                key={asset.id}
                className="group relative h-20 w-20 overflow-hidden rounded-lg border border-border"
              >
                {asset.mime_type.startsWith('video/') ? (
                  <video src={asset.public_url || undefined} className="h-full w-full object-cover" muted />
                ) : (
                  <img src={asset.public_url || undefined} alt="" className="h-full w-full object-cover" />
                )}
                <span className="absolute left-1 top-1 rounded bg-foreground/60 px-1 text-[10px] text-background">
                  {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => props.onRemoveMedia(asset.id)}
                  className="absolute right-1 top-1 rounded bg-foreground/60 p-0.5 text-background opacity-0 transition group-hover:opacity-100"
                  aria-label="Medium entfernen"
                >
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="grid gap-4 sm:grid-cols-2">
        <div>
          <LabelWithHint htmlFor="composer-link" label="Link" hint="Wird als Link-Post genutzt, wenn kein Medium gewählt ist." />
          <input
            id="composer-link"
            value={props.linkUrl}
            onChange={(e) => props.setLinkUrl(e.target.value)}
            className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
            placeholder="https://"
          />
        </div>
        <div>
          <LabelWithHint htmlFor="composer-schedule" label="Geplant für" hint="Setzt Termin im Planner. „Planen“ reiht die Veröffentlichung ein. Fällt der Termin in die Ruhezeit, verschiebt der Server ihn ans Ende des Fensters." />
          <input
            id="composer-schedule"
            type="datetime-local"
            value={props.scheduledAt}
            onChange={(e) => props.setScheduledAt(e.target.value)}
            className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
          />
          <ScheduleAssist
            platform={props.platforms[0]}
            onPick={(iso) => props.setScheduledAt(toLocalInput(iso))}
          />
        </div>
      </section>

      <section>
        <LabelWithHint htmlFor="composer-comment" label="Erster Kommentar" hint="Für Links oder Hashtags, die nicht in den Haupttext sollen (LinkedIn/Instagram)." />
        <textarea
          id="composer-comment"
          value={props.firstComment}
          onChange={(e) => props.setFirstComment(e.target.value)}
          rows={2}
          className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
          placeholder="Optional"
        />
      </section>

      {props.pillars.length > 0 && (
        <section>
          <LabelWithHint label="Content-Säule" hint="Ordnet den Beitrag im Planner ein und hält die Themenmischung sichtbar." />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {props.pillars.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => props.setPillarId(props.pillarId === p.id ? null : p.id)}
                className={cn(
                  'flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition',
                  props.pillarId === p.id
                    ? 'border-border bg-primary text-primary-foreground'
                    : 'border-border text-muted'
                )}
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
                {p.name}
              </button>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function ChannelEditor({
  channel,
  baseBody,
  baseTitle,
  warnings,
  onPatch,
  onPatchMeta,
  onRetry,
  onPublish,
  busy,
  readOnly,
}: {
  channel: ChannelDraft;
  baseBody: string;
  baseTitle: string;
  warnings: ChannelWarning[];
  onPatch: (patch: Partial<ChannelDraft>) => void;
  onPatchMeta: (patch: SocialTargetMetadata) => void;
  onRetry: () => void;
  onPublish: () => void;
  busy: boolean;
  readOnly: boolean;
}) {
  const meta = platformMeta(channel.platform);
  const effectiveBody = channel.bodyOverride ?? baseBody;
  const overLimit = effectiveBody.length > meta.textLimit;
  const published = channel.status === 'published';
  const locked = readOnly || published;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-background/70 p-4 dark:bg-card/40">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <PlatformIcon platform={channel.platform} size={28} />
            <span className="text-sm font-medium text-foreground">{meta.label}</span>
            <span className="text-sm text-muted">{channel.accountName}</span>
          </div>
          <div className="flex gap-2">
            {channel.status === 'failed' && (
              <Button variant="outline" size="sm" onClick={onRetry} disabled={busy}>
                <RotateCcw size={13} className="mr-1.5" /> Erneut versuchen
              </Button>
            )}
            {channel.externalUrl && (
              <a
                href={channel.externalUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted hover:bg-card-hover"
              >
                <ExternalLink size={12} /> Ansehen
              </a>
            )}
            {!published && channel.status !== 'publishing' && (
              <Button size="sm" onClick={onPublish} disabled={busy || warnings.some((warning) => warning.level === 'error')}>
                <Send size={13} className="mr-1.5" /> Nur hier posten
              </Button>
            )}
          </div>
        </div>
        <p className="mt-2 text-xs text-muted">{meta.hint}</p>
        {channel.errorMessage && (
          <p className="mt-2 rounded-lg bg-danger-soft px-2.5 py-1.5 text-xs text-danger">
            {channel.errorMessage}
          </p>
        )}
      </div>

      {warnings.length > 0 && (
        <ul className="space-y-1">
          {warnings.map((w) => (
            <li
              key={w.message}
              className={cn(
                'flex items-start gap-1.5 rounded-lg px-3 py-2 text-xs',
                w.level === 'error'
                  ? 'bg-danger-soft text-danger'
                  : 'bg-warning-soft text-warning'
              )}
            >
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              {w.message}
            </li>
          ))}
        </ul>
      )}

      <section>
        <LabelWithHint label="Post-Art für diesen Kanal" hint="Überschreibt die Basis-Einstellung nur hier." />
        <div className="mt-2 flex flex-wrap gap-2">
          {meta.kinds.map((k) => (
            <button
              key={k}
              type="button"
              disabled={locked}
              onClick={() => onPatch({ kind: k })}
              className={cn(
                'rounded-lg border px-3 py-1.5 text-sm font-medium transition',
                channel.kind === k
                  ? 'border-border bg-primary text-primary-foreground'
                  : 'border-border text-muted',
                published && 'cursor-not-allowed opacity-50'
              )}
            >
              {POST_KIND_LABEL[k]}
            </button>
          ))}
        </div>
      </section>

      {meta.titleRequired && (
        <section>
          <LabelWithHint
            label="Titel"
            hint={
              channel.platform === 'youtube'
                ? 'Max. 100 Zeichen. Bei Shorts wird automatisch #Shorts ergänzt.'
                : 'Max. 300 Zeichen. Der Titel entscheidet auf Reddit über Klicks.'
            }
          />
          <input
            value={channel.titleOverride ?? baseTitle}
            disabled={locked}
            onChange={(e) => onPatch({ titleOverride: e.target.value })}
            className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm disabled:opacity-60"
            maxLength={channel.platform === 'youtube' ? 100 : 300}
          />
        </section>
      )}

      <section>
        <div className="flex items-end justify-between gap-2">
          <LabelWithHint
            label="Text für diesen Kanal"
            hint="Leer lassen bzw. zurücksetzen, um den Basistext zu verwenden."
          />
          {channel.bodyOverride !== null && (
            <Button variant="ghost" size="sm" disabled={locked} onClick={() => onPatch({ bodyOverride: null })}>
              <Trash2 size={13} className="mr-1.5" /> Auf Basistext zurücksetzen
            </Button>
          )}
        </div>
        <textarea
          value={effectiveBody}
          disabled={locked}
          onChange={(e) => onPatch({ bodyOverride: e.target.value })}
          rows={10}
          className="mt-2 w-full rounded-lg border border-border bg-card px-4 py-3 text-sm leading-relaxed disabled:opacity-60"
        />
        <p className={cn('mt-1 text-xs', overLimit ? 'font-medium text-danger' : 'text-muted')}>
          {effectiveBody.length} / {meta.textLimit} Zeichen
          {channel.bodyOverride === null && ' · folgt dem Basistext'}
        </p>
      </section>

      {channel.platform === 'reddit' && (
        <section className="space-y-4 rounded-lg border border-border p-4">
          <div>
            <LabelWithHint
              label="Subreddit"
              hint="Ohne „r/“ oder mit — beides geht. Prüfe vorher die Regeln des Subreddits."
            />
            <input
              value={channel.metadata.subreddit ?? ''}
              disabled={locked}
              onChange={(e) => onPatchMeta({ subreddit: e.target.value })}
              placeholder="z. B. r/marketing"
              className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
            />
          </div>
          <div>
            <LabelWithHint label="Flair" hint="Manche Subreddits verlangen ein Flair. ID aus den Subreddit-Einstellungen." />
            <input
              value={channel.metadata.flair_text ?? ''}
              disabled={locked}
              onChange={(e) => onPatchMeta({ flair_text: e.target.value })}
              placeholder="Optional"
              className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-4">
            <Toggle
              label="NSFW"
              checked={channel.metadata.nsfw === true}
              onChange={(v) => onPatchMeta({ nsfw: v })}
              disabled={locked}
            />
            <Toggle
              label="Spoiler"
              checked={channel.metadata.spoiler === true}
              onChange={(v) => onPatchMeta({ spoiler: v })}
              disabled={locked}
            />
            <Toggle
              label="Antworten ins Postfach"
              checked={channel.metadata.send_replies !== false}
              onChange={(v) => onPatchMeta({ send_replies: v })}
              disabled={locked}
            />
          </div>
        </section>
      )}

      {channel.platform === 'pinterest' && (
        <section className="space-y-4 rounded-lg border border-border p-4">
          <div>
            <LabelWithHint
              label="Board"
              hint="Pin-Ziel. Leer lassen, wenn beim Verbinden bereits ein Standard-Board gewählt wurde."
            />
            <input
              value={channel.metadata.board_id ?? ''}
              disabled={locked}
              onChange={(e) => onPatchMeta({ board_id: e.target.value })}
              placeholder="Board-ID"
              className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
            />
          </div>
          <div>
            <LabelWithHint label="Ziel-Link" hint="Wohin der Pin führt. Ohne Angabe wird der Link des Beitrags verwendet." />
            <input
              value={channel.metadata.destination_url ?? ''}
              disabled={locked}
              onChange={(e) => onPatchMeta({ destination_url: e.target.value })}
              placeholder="https://…"
              className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
            />
          </div>
        </section>
      )}

      {channel.platform === 'google_business' && (
        <section className="space-y-4 rounded-lg border border-border p-4">
          <div>
            <LabelWithHint
              label="Handlungsaufruf"
              hint="Button unter dem Beitrag im Unternehmensprofil. Ohne Link bleibt der Beitrag ohne Button."
            />
            <select
              value={channel.metadata.cta_type ?? ''}
              disabled={locked}
              onChange={(e) =>
                onPatchMeta({
                  cta_type: (e.target.value || undefined) as SocialTargetMetadata['cta_type'],
                })
              }
              className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
            >
              <option value="">Kein Button</option>
              <option value="LEARN_MORE">Mehr erfahren</option>
              <option value="BOOK">Termin buchen</option>
              <option value="ORDER">Bestellen</option>
              <option value="SHOP">Shop ansehen</option>
              <option value="SIGN_UP">Registrieren</option>
              <option value="CALL">Anrufen</option>
            </select>
          </div>
          <div>
            <LabelWithHint label="Sprache" hint="Sprachcode des Beitrags, z. B. de-CH. Standard ist de-CH." />
            <input
              value={channel.metadata.language_code ?? ''}
              disabled={locked}
              onChange={(e) => onPatchMeta({ language_code: e.target.value })}
              placeholder="de-CH"
              className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm"
            />
          </div>
        </section>
      )}

      {channel.platform === 'tiktok' && (
        <section className="space-y-3 rounded-lg border border-border p-4">
          <LabelWithHint
            label="TikTok-Optionen"
            hint="Sichtbarkeit steuert der Server: ohne abgeschlossenes TikTok-Audit wird SELF_ONLY erzwungen."
          />
          <div className="flex flex-wrap gap-4">
            <Toggle
              label="Kommentare aus"
              checked={channel.metadata.disable_comment === true}
              onChange={(v) => onPatchMeta({ disable_comment: v })}
              disabled={locked}
            />
            <Toggle
              label="Duette aus"
              checked={channel.metadata.disable_duet === true}
              onChange={(v) => onPatchMeta({ disable_duet: v })}
              disabled={locked}
            />
            <Toggle
              label="Stitch aus"
              checked={channel.metadata.disable_stitch === true}
              onChange={(v) => onPatchMeta({ disable_stitch: v })}
              disabled={locked}
            />
          </div>
        </section>
      )}

      {channel.platform === 'youtube' && (
        <section className="rounded-lg border border-border p-4">
          <LabelWithHint
            label="Sichtbarkeit"
            hint="Neue Uploads starten sicherheitshalber als „privat“ — erst prüfen, dann öffentlich schalten."
          />
          <div className="mt-2 flex gap-2">
            {(['private', 'unlisted', 'public'] as const).map((v) => (
              <button
                key={v}
                type="button"
                disabled={locked}
                onClick={() => onPatch({ privacy: v })}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-sm capitalize transition',
                  (channel.privacy ?? 'private') === v
                    ? 'border-border bg-primary text-primary-foreground'
                    : 'border-border text-muted',
                  published && 'cursor-not-allowed opacity-50'
                )}
              >
                {v === 'private' ? 'Privat' : v === 'unlisted' ? 'Nicht gelistet' : 'Öffentlich'}
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-muted">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-border text-foreground"
      />
      {label}
    </label>
  );
}

function PreviewPane({
  channels,
  activeChannel,
  baseBody,
  baseTitle,
  media,
  boardStatus,
  metrics,
}: {
  channels: ChannelDraft[];
  activeChannel: ChannelDraft | null;
  baseBody: string;
  baseTitle: string;
  media: MediaAsset[];
  boardStatus: SocialPost['board_status'];
  metrics: Array<{ target_id: string; platform?: string; analytics?: Record<string, number> }>;
}) {
  const shown = activeChannel ?? channels[0] ?? null;
  const meta = shown ? platformMeta(shown.platform) : null;
  const previewBody = shown ? (shown.bodyOverride ?? baseBody) : baseBody;
  const previewTitle = shown ? (shown.titleOverride ?? baseTitle) : baseTitle;

  return (
    <aside className="space-y-4">
      <div className="rounded-lg border border-border bg-background p-4 dark:bg-card/50">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">Vorschau</h2>
          {boardStatus && (
            <span className="rounded-full bg-card-hover px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted">
              {boardStatus}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-muted">
          {meta ? `${meta.label} · ${POST_KIND_LABEL[shown!.kind]}` : 'Noch kein Kanal gewählt'}
        </p>

        <div className="mt-3 overflow-hidden rounded-lg border border-border bg-card dark:bg-background">
          {media[0] && (
            <div className="aspect-square w-full bg-card-hover dark:bg-card">
              {media[0].mime_type.startsWith('video/') ? (
                <video src={media[0].public_url || undefined} className="h-full w-full object-cover" muted />
              ) : (
                <img src={media[0].public_url || undefined} alt="" className="h-full w-full object-cover" />
              )}
            </div>
          )}
          <div className="p-3">
            {previewTitle && (
              <p className="mb-1.5 text-sm font-semibold text-foreground">{previewTitle}</p>
            )}
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted">
              {previewBody || 'Dein Text erscheint hier…'}
            </p>
          </div>
        </div>
      </div>

      {channels.length > 0 && (
        <div className="rounded-lg border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground">Kanal-Status</h3>
          <ul className="mt-2 space-y-1.5">
            {channels.map((c) => (
              <li key={c.accountId} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-muted">
                  {platformMeta(c.platform).label} · {c.accountName}
                </span>
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2 py-0.5 font-medium',
                    c.status === 'published'
                      ? 'bg-success-soft text-success'
                      : c.status === 'failed'
                        ? 'bg-danger-soft text-danger'
                        : c.status === 'scheduled'
                          ? 'bg-accent-soft text-accent'
                          : 'bg-card-hover text-muted'
                  )}
                >
                  {c.status ?? 'neu'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {metrics.length > 0 && (
        <div className="rounded-lg border border-border p-4">
          <h2 className="text-sm font-semibold text-foreground">Performance</h2>
          <ul className="mt-2 space-y-1 text-sm">
            {metrics.map((row) => {
              const a = row.analytics ?? {};
              const impressions = a.impressions || a.reach || a.views || 0;
              const engagement = (a.likes || 0) + (a.comments || 0) + (a.shares || 0) + (a.clicks || 0);
              return (
                <li key={row.target_id} className="flex justify-between gap-2 text-muted">
                  <span>{row.platform ?? 'Kanal'}</span>
                  <span className="tabular-nums">
                    {formatMetric(impressions)} · {formatMetric(engagement)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </aside>
  );
}
